'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { Calendar } from '@eliadmin/core/src/components/ui/calendar';
import { addDays, startOfWeek, endOfWeek, format, isBefore, isEqual } from 'date-fns';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@eliadmin/core/src/components/ui/hover-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useState } from 'react';

type ReservationDatepickerProps = {
  dateFrom: Date;
  dateTo: Date;
  selectedDate: Date;
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  onDateChange: (dateFrom: Date, dateTo: Date, selectedDate?: Date) => void;
};

export const ReservationDatepicker = ({
  onDateChange,
  dateFrom,
  dateTo,
  selectedDate: selectedDateProp,
  weekStartsOn = 1,
}: ReservationDatepickerProps) => {
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [selectedDate, setSelectedDate] = useState<Date>(() => selectedDateProp);
  const minWeekStart = startOfWeek(new Date(), { weekStartsOn });
  const isAtMinWeek = isBefore(dateFrom, minWeekStart) || isEqual(dateFrom, minWeekStart);

  // Keep internal state aligned with the first available day resolved by the slots fetch.
  useEffect(() => {
    setSelectedDate(selectedDateProp);
  }, [selectedDateProp]);

  const previousWeek = () => {
    if (isAtMinWeek) return;
    const previousWeekSameDay = addDays(dateFrom, -7);
    const dateFromPreviousWeek = startOfWeek(previousWeekSameDay, { weekStartsOn });
    if (isBefore(dateFromPreviousWeek, minWeekStart)) return;
    setSelectedDate(previousWeekSameDay);
    const dateToPreviousWeek = endOfWeek(previousWeekSameDay, { weekStartsOn });
    onDateChange(dateFromPreviousWeek, dateToPreviousWeek, previousWeekSameDay);
  };

  const nextWeek = () => {
    const nextWeekSameDay = addDays(selectedDate, 7);
    setSelectedDate(nextWeekSameDay);
    const dateFromNextWeek = startOfWeek(nextWeekSameDay, { weekStartsOn });
    const dateToNextWeek = endOfWeek(nextWeekSameDay, { weekStartsOn });
    onDateChange(dateFromNextWeek, dateToNextWeek, nextWeekSameDay);
  };

  const selectWeek = (date: Date) => {
    const dateFrom = startOfWeek(date, { weekStartsOn });
    const dateTo = endOfWeek(date, { weekStartsOn });
    if (isBefore(dateFrom, minWeekStart)) return;
    setSelectedDate(date);
    onDateChange(dateFrom, dateTo, date);
    setIsOpen(false);
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-2 sm:justify-center',
        'ea-reservation-datepicker',
      )}
    >
      <Button
        aria-label="Predchádzajúci týždeň"
        onClick={() => previousWeek()}
        type="button"
        disabled={isAtMinWeek}
        className={cn(
          'h-12 w-12 rounded-full border border-black/10 bg-transparent p-0 hover:bg-black/10',
          'ea-reservation-datepicker__previous-week-button ea-reservation-datepicker__change-week-button',
        )}
      >
        <Image
          src="/images/arrow-right.svg"
          alt=""
          height={20}
          width={20}
          className="scale-x-[-1] object-contain"
        />
      </Button>
      <h2
        className={cn(
          'w-[120px] text-center font-semibold',
          'ea-reservation-datepicker__date-range',
        )}
      >
        {format(dateFrom, 'dd.MM')} - {format(dateTo, 'dd.MM')}
      </h2>
      <Button
        aria-label="Nasledujúci týždeň"
        onClick={() => nextWeek()}
        type="button"
        className={cn(
          'h-12 w-12 rounded-full border border-black/10 bg-transparent p-0 hover:bg-black/10',
          'ea-reservation-datepicker__next-week-button ea-reservation-datepicker__change-week-button',
        )}
      >
        <Image
          src="/images/arrow-right.svg"
          alt=""
          height={20}
          width={20}
          className="object-contain"
        />
      </Button>
      <HoverCard openDelay={0} open={isOpen} onOpenChange={setIsOpen} closeDelay={100}>
        <HoverCardTrigger className={cn('', 'ea-reservation-datepicker__hover-trigger')} asChild>
          <Button
            aria-label="Vybrať dátum"
            type="button"
            className={cn(
              'h-12 w-12 rounded-full border border-black/10 bg-transparent p-0 hover:bg-black/10',
              'ea-reservation-datepicker__hover-trigger-button',
            )}
          >
            <Image
              src="/images/calendar-add.svg"
              alt=""
              height={20}
              width={20}
              className="object-contain"
            />
          </Button>
        </HoverCardTrigger>
        <HoverCardContent className={cn('w-auto', 'ea-reservation-datepicker__hover-content')}>
          <Calendar
            onDayClick={selectWeek}
            selected={selectedDate}
            disabled={{ before: minWeekStart }}
            className={cn('w-auto', 'ea-reservation-datepicker__hover-calendar')}
          />
        </HoverCardContent>
      </HoverCard>
    </div>
  );
};
